import React, { Suspense, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { ThemeProvider as EmotionThemeProvider } from "@emotion/react";
import Loadable from "@loadable/component";
import CssBaseline from "@material-ui/core/CssBaseline";
import { ThemeProvider } from "@material-ui/core/styles";

import { observeForms } from "api/forms";
import PosApi from "api/pos";
import { observe as observeShifts } from "api/shifts";
import { observe as observeStatuses } from "api/booking-statuses";
import { observe as observeEvents } from "api/events";
import CustomerAuthProvider from "common/context/CustomerAuthContext";
import { useBreakpoints } from "common/hooks";
import Loader from "components/Loader";
import FourOhFourPage from "pages/FourOhFourPage";
import { DesktopLoader, MobileLoader } from "pages/restaurant/Loaders";
import OrderProvider from "pages/restaurant/order/context";
import { getBusiness } from "reducers/businessReducer";
import useBusiness from "reducers/hooks/useBusiness";
import useObserveActiveFloorplan from "reducers/hooks/useObserveActiveFloorplan";
import useObserveCurrentWaitTime from "reducers/hooks/useObserveCurrentWaitTime";
import useObserveLinkClicks from "reducers/hooks/useObserveLinkClicks";
import useParams from "reducers/hooks/useParams";
import { ObserveRouter } from "reducers/hooks/useParams";
import { loadStyle } from "util";
import Api from "util/api";

import useLogPageView from "./useLogPageView";
import useTheme from "./useTheme";

// import "./index.scss";
// import "./index.scss";

const Desktop = Loadable(
  () =>
    import(
      /* webpackChunkName: "RestaurantDesktopPage" */ "pages/restaurant/desktop/index.jsx"
    ),
  {
    fallback: <DesktopLoader />,
  }
);
const Mobile = Loadable(
  () =>
    import(
      /* webpackChunkName: "RestaurantMobilePage" */ "pages/restaurant/mobile/index.jsx"
    ),
  {
    fallback: <MobileLoader />,
  }
);

function ScrollToTop(props) {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return props.children;
}

function selectPageEnabled(state) {
  const business = getBusiness(state);
  if (!business) return false;
  if (business.hasOwnProperty("active") && !business.active) return false;
  if (business.hasOwnProperty("restaurantPageEnabled")) {
    return business.restaurantPageEnabled;
  }
  return true;
}

function Inner() {
  const bp = useBreakpoints();
  const loading = useSelector((state) => state.business.loading);
  const error = useSelector((state) => state.business.error);
  const restaurantPageEnabled = useSelector(selectPageEnabled);

  if (loading) {
    if (bp.lessThan.lg) return <MobileLoader />;
    return <DesktopLoader />;
  }
  if (error || !restaurantPageEnabled) return <FourOhFourPage />;

  if (bp.lessThan.lg) return <Mobile />;
  return <Desktop />;
}
const InnerMemo = React.memo(Inner);

function RestaurantPage() {
  const businessUid = useParams(["id"]).id;
  useBusiness();
  useObserveActiveFloorplan();
  useObserveCurrentWaitTime();
  useObserveLinkClicks();
  useLogPageView();

  useEffect(() => {
    Api.fetchTags(businessUid);

    const unsubscribeDiscounts = PosApi.observeDiscounts(businessUid);
    const unsubscribeMenuItems = PosApi.observeMenuItems(businessUid);
    const unsubscribeMenus = PosApi.observeMenus(businessUid);
    const unsubscribeModifiers = PosApi.observeModifiers(businessUid);
    const unsubscribeSurcharges = PosApi.observeSurcharges(businessUid);
    const unsubscribeShifts = observeShifts(businessUid);
    const unsubscribeStatuses = observeStatuses(businessUid);
    const unsubscribeTaxes = PosApi.observeTaxes(businessUid);
    const unsubscribeBlockedTables = Api.observeBlockTables(businessUid);
    const unSubForms = observeForms(businessUid);
    const unsubscribeEvents = observeEvents(businessUid);

    return () => {
      unsubscribeDiscounts();
      unsubscribeMenuItems();
      unsubscribeMenus();
      unsubscribeModifiers();
      unsubscribeSurcharges();
      unsubscribeShifts();
      unsubscribeTaxes();
      unsubscribeBlockedTables();
      unsubscribeStatuses();
      unSubForms();
      unsubscribeEvents();
    };
  }, [businessUid]);

  return <InnerMemo />;
}

function Wrapper() {
  const theme = useTheme();

  useEffect(() => {
    // loadStyle(
    //   "https://fonts.googleapis.com/css2?family=Open+Sans:wght@100;300;400;500;600;700&display=swap"
    // );
    loadStyle(
      "https://fonts.googleapis.com/css2?family=Open+Sans:wght@100;200;300;400;500;600;700;800&display=swap"
    );
  }, []);

  return (
    <Suspense fallback={<Loader />}>
      <ScrollToTop>
        <ObserveRouter>
          <ThemeProvider theme={theme}>
            <EmotionThemeProvider theme={theme}>
              <CustomerAuthProvider>
                <OrderProvider>
                  <CssBaseline />
                  <RestaurantPage />
                </OrderProvider>
              </CustomerAuthProvider>
            </EmotionThemeProvider>
          </ThemeProvider>
        </ObserveRouter>
      </ScrollToTop>
    </Suspense>
  );
}

export default React.memo(Wrapper);
